import React, { useMemo, useState } from "react";
import {
  AppBar,
  Box,
  Container,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

const policyData = {
  privacy: {
    title: "Privacy Policy",
    subtitle: "How Sakhi's Shop collects, uses, and protects shop and customer data.",
    sections: [
      {
        heading: "Information We Collect",
        text: "When you use Sakhi's Shop, we may collect personal and business details you provide during account setup, orders, payments, and support requests. We may also collect technical details like IP address, device/browser type, and session metadata for security and service improvement.",
      },
      {
        heading: "Consent and Use of Information",
        text: "Data is used to operate the app, verify transactions, manage orders, and provide customer support. If we need data for optional communications such as promotions, we request consent where applicable. You can contact us anytime to update or withdraw communication preferences.",
      },
      {
        heading: "Data Security",
        text: "We use administrative and technical safeguards to protect information from unauthorized access, misuse, or loss. Access is restricted to authorized staff and systems that need the data for app operations.",
      },
      {
        heading: "Retention and Contact",
        text: "We retain information only as long as required for business, legal, and operational purposes. For privacy-related requests, contact: saarasofttech@gmail.com.",
      },
    ],
  },
  refund: {
    title: "Refund Policy",
    subtitle: "Rules for refunds, cancellations, and settlement of payments.",
    sections: [
      {
        heading: "Order Cancellations",
        text: "Cancellation eligibility depends on order status. If an order is already processed or dispatched, cancellation may not be possible. Approved cancellations are reflected using the original payment method where available.",
      },
      {
        heading: "Refund Timeline",
        text: "Once approved, refunds are initiated promptly. Bank and payment gateway processing times may vary. Please allow a reasonable processing window before raising a dispute.",
      },
      {
        heading: "Non-Refundable Cases",
        text: "Certain service charges, already consumed services, or misuse-related actions may not qualify for a refund. Final eligibility is decided after verification of order and account records.",
      },
      {
        heading: "Support",
        text: "For refund help, contact support with order details and registered mobile/email for faster resolution.",
      },
    ],
  },
  appUsage: {
    title: "App Usage Terms",
    subtitle: "Guidelines for acceptable use of Sakhi's Shop services.",
    sections: [
      {
        heading: "Account Responsibility",
        text: "You are responsible for maintaining the confidentiality of your login credentials and all activities performed through your account. Share credentials only with trusted authorized users.",
      },
      {
        heading: "Acceptable Use",
        text: "Users must provide accurate information, comply with applicable laws, and avoid fraudulent or abusive activity. Any misuse may result in temporary suspension or permanent account action.",
      },
      {
        heading: "Service Availability",
        text: "We continuously improve the platform and may update, add, or remove features when required for performance, maintenance, or compliance reasons.",
      },
      {
        heading: "Policy Acceptance",
        text: "By creating an account or using the app, you agree to these terms along with the Privacy Policy and Refund Policy.",
      },
    ],
  },
};

const tabs = [
  { key: "privacy", label: "Privacy Policy" },
  { key: "refund", label: "Refund Policy" },
  { key: "appUsage", label: "App Usage" },
];

const PoliciesLegal = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("privacy");

  const activePolicy = useMemo(() => policyData[activeTab], [activeTab]);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#fafafa" }}>
      <AppBar position="sticky" color="default" elevation={1} sx={{ backgroundColor: "#fff" }}>
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate(-1)} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Policies & Legal
          </Typography>
        </Toolbar>
        <Tabs
          value={activeTab}
          onChange={(event, nextValue) => setActiveTab(nextValue)}
          variant="scrollable"
          scrollButtons="auto"
          textColor="inherit"
          indicatorColor="primary"
        >
          {tabs.map((tab) => (
            <Tab key={tab.key} value={tab.key} label={tab.label} />
          ))}
        </Tabs>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
          {activePolicy.title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {activePolicy.subtitle}
        </Typography>

        {activePolicy.sections.map((section) => (
          <Paper
            key={section.heading}
            elevation={0}
            sx={{
              mb: 2,
              p: 3,
              borderRadius: 3,
              border: "1px solid #e7e7e7",
              backgroundColor: "#f7f7f0",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.2 }}>
              {section.heading}
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              {section.text}
            </Typography>
          </Paper>
        ))}
      </Container>
    </Box>
  );
};

export default PoliciesLegal;
